/**
 * 学習履歴画面
 * 正答率・カテゴリ別統計・回答履歴一覧を表示する
 */

import React, { useState } from "react";
import {
  BarChart2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  BookOpen,
  TrendingUp,
  Target,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useHistory, HistoryItem, CategoryStat } from "../../../hooks/useHistory";
import { useAuth } from "../../../contexts/AuthContext";

/**
 * 正答率に応じた色を返す
 */
function getRateColor(rate: number): string {
  if (rate >= 0.8) return "text-emerald-600";
  if (rate >= 0.6) return "text-sky-600";
  if (rate >= 0.4) return "text-amber-600";
  return "text-red-500";
}

/**
 * 正答率バーの色を返す
 */
function getRateBarColor(rate: number): string {
  if (rate >= 0.8) return "bg-emerald-500";
  if (rate >= 0.6) return "bg-sky-500";
  if (rate >= 0.4) return "bg-amber-500";
  return "bg-red-500";
}

/**
 * 日時を日本語フォーマットに変換
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * カテゴリ別統計カード
 */
const CategoryStatCard: React.FC<{ stat: CategoryStat }> = ({ stat }) => {
  const rate = stat.correctRate;
  const pct = Math.round(rate * 100);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-900 dark:text-white truncate max-w-[60%]">
          {stat.category || "未分類"}
        </span>
        <span className={`text-sm font-bold ${getRateColor(rate)}`}>
          {pct}%
        </span>
      </div>
      <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2 mb-1">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${getRateBarColor(rate)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400">
        {stat.correct}/{stat.total} 問正解
      </div>
    </div>
  );
};

/**
 * 履歴アイテム行
 */
const HistoryRow: React.FC<{ item: HistoryItem; index: number }> = ({
  item,
  index,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-slate-200 dark:border-slate-600 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        {/* 正誤アイコン */}
        <div className="flex-shrink-0 mt-0.5">
          {item.isCorrect ? (
            <CheckCircle className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
        </div>

        {/* 問題テキスト */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-900 dark:text-white line-clamp-2 font-medium">
            {item.questionText}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {item.category && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                {item.category}
              </span>
            )}
            <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(item.answeredAt)}
            </span>
          </div>
        </div>

        {/* 展開アイコン */}
        <div className="flex-shrink-0 text-slate-400 dark:text-slate-500">
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </button>

      {/* 展開時: 詳細 */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700">
          <div className="pt-3 text-sm text-slate-700 dark:text-slate-300">
            <span className="font-medium">あなたの回答: </span>
            <span
              className={item.isCorrect ? "text-emerald-600" : "text-red-500"}
            >
              選択肢 {["ア", "イ", "ウ", "エ"][item.selectedAnswer] ?? item.selectedAnswer + 1}
            </span>
            {!item.isCorrect && (
              <span className="ml-2 text-slate-500 dark:text-slate-400">（不正解）</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * 学習履歴ページ本体
 */
export const HistoryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { history, statistics, isLoading, error, refetch } = useHistory(
    user?.idToken,
  );

  const [showAll, setShowAll] = useState(false);
  const displayHistory = showAll ? history : history.slice(0, 20);

  const BackLink = () => (
    <button
      onClick={() => navigate("/mypage")}
      className="inline-flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 font-medium"
    >
      &larr; マイページに戻る
    </button>
  );

  // ローディング
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-slate-900 dark:text-white animate-spin mb-4" />
        <p className="text-slate-500 dark:text-slate-400">学習履歴を読み込み中...</p>
      </div>
    );
  }

  // エラー
  if (error) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-600 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            履歴の読み込みに失敗しました
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            再試行
          </button>
        </div>
      </div>
    );
  }

  // 履歴なし
  if (!statistics || statistics.totalAnswers === 0) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-12 shadow-sm border border-slate-200 dark:border-slate-600 text-center">
          <BookOpen className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            まだ学習履歴がありません
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            問題に回答すると、ここに学習履歴が表示されます。
          </p>
        </div>
      </div>
    );
  }

  const overallPct = Math.round(statistics.correctRate * 100);

  return (
    <div className="space-y-6">
      <BackLink />

      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6" />
          学習履歴
        </h2>
        <button
          onClick={refetch}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          aria-label="更新"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          更新
        </button>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* 総回答数 */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-sm border border-slate-200 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              総回答数
            </span>
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white">
            {statistics.totalAnswers}
            <span className="text-base font-normal text-slate-500 dark:text-slate-400 ml-1">問</span>
          </div>
        </div>

        {/* 正解数 */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-sm border border-slate-200 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              正解数
            </span>
          </div>
          <div className="text-3xl font-bold text-emerald-600">
            {statistics.correctAnswers}
            <span className="text-base font-normal text-slate-500 dark:text-slate-400 ml-1">問</span>
          </div>
        </div>

        {/* 正答率 */}
        <div className="col-span-2 md:col-span-1 bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-sm border border-slate-200 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              正答率
            </span>
          </div>
          <div className={`text-3xl font-bold ${getRateColor(statistics.correctRate)}`}>
            {overallPct}
            <span className="text-base font-normal text-slate-500 dark:text-slate-400 ml-0.5">%</span>
          </div>
          <div className="mt-2 w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-700 ${getRateBarColor(statistics.correctRate)}`}
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* カテゴリ別統計 */}
      {statistics.categoryStats.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-600">
          <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart2 className="w-4 h-4" />
            分野別正答率
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {statistics.categoryStats.map((stat) => (
              <CategoryStatCard key={stat.category} stat={stat} />
            ))}
          </div>
        </div>
      )}

      {/* 回答履歴一覧 */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-600">
        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          回答履歴
          <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
            （直近 {history.length} 件）
          </span>
        </h3>

        <div className="space-y-2">
          {displayHistory.map((item, index) => (
            <HistoryRow key={item.id} item={item} index={index} />
          ))}
        </div>

        {history.length > 20 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="mt-4 w-full py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-4 h-4" />
                折りたたむ
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                すべて表示（{history.length} 件）
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
